/**
 * The upload REFUSAL POLICY — the single runtime copy, as plain ESM.
 *
 * 🚨 **THIS IS THE SOURCE, NOT A MIRROR.** `upload-safeguards.ts` re-exports
 * from here and adds only types; there is exactly ONE implementation of these
 * rules in the tree. That distinction is the whole point of the file and it is
 * what separates it from `tests/upload-matrix/lib/ingest-lines.mjs`, which IS a
 * mirror of a runtime-owned contract and carries a drift self-test for that
 * reason. Nothing here needs a drift test, because there is nothing to drift
 * from.
 *
 * ⚠ **ZERO IMPORTS, AND THE PROPERTY IS LOAD-BEARING.** An out-of-process
 * driver (`ui-tester`'s Node runner, this repo's `tests/upload-matrix/*.mjs`)
 * consumes this file directly, with no bundler, no `@/` alias and no TS. Adding
 * an import here breaks a consumer that chose this module precisely because it
 * has none. The `DocumentUploadLimits` type lives in the `.d.mts` beside this
 * file and erases at runtime.
 *
 * ⚠ **WHY IT WAS SPLIT** (`ui-tester` handshake, 2026-09-13): their upload
 * runner could not tell "the SPA accepted this file" from "the CDP call
 * returned", and re-deriving this policy in the runner would have given the
 * fleet a SECOND, private copy that goes stale the first time the archive or
 * httpUpload rules move. The policy is not a flat size check — it is
 * denylist -> WS ceiling -> httpUpload presence/archiveOnly -> containerExceeded
 * -> per-type cap, with `effectiveMaxBytes = min(typeCap, transportCap)` and a
 * T-5 archive exception on top.
 *
 * THE SERVER STAYS AUTHORITATIVE. Everything here is advisory and trivially
 * bypassable — its only job is to fail FAST with a good message instead of
 * base64-ing 40 MB over the wire to earn a rejection. Order mirrors the
 * server's deliberately (denylist -> transport -> per-type), because the order
 * is observable: it decides WHICH limit the user is told about, and telling
 * them the wrong one is worse than saying nothing.
 *
 * Pure + IO-free so the rules are unit-testable without a gateway.
 */

/** Lowercased extension including the dot, or "" when the name has none. */
export function extensionOf(fileName) {
  const dot = fileName.lastIndexOf(".");
  return dot > 0 ? fileName.slice(dot).toLowerCase() : "";
}

/** Extensions that ride the HTTP container path. The runtime gates archives by
 *  its own list; `.zip` is the only one it accepts today and the only one the
 *  picker offers, so this stays deliberately narrow rather than guessing at a
 *  vocabulary the server owns. */
const ARCHIVE_EXTS = new Set([".zip"]);

/**
 * The limit that ACTUALLY applies to a file, on whichever transport it will use.
 *
 * ⚠ The transport ceiling is checked BEFORE the per-type cap, and it is
 * LOWER than several of them (18 MB transport vs 25 MB docs, 100 MB zip,
 * 200 MB video). Those larger caps are therefore unreachable over the WS:
 * a 30 MB video is refused at 18 MB, not 200 MB. Advertising the per-type
 * number would promise a limit the transport cannot honour, so a WS-bound file
 * must be quoted the MINIMUM of the two.
 *
 * ⚖ **T-5 exception:** an ARCHIVE is not WS-bound once `httpUpload` is served —
 * it rides the container POST, so its ceiling is `maxContainerBytes` and quoting
 * the WS number would UNDERSTATE it. Both directions of wrongness matter here:
 * overstating promises a limit the transport cannot honour, understating stops a
 * lawyer trying an upload that would have worked.
 *
 * (The larger per-type caps also apply to archive MEMBERS, which are sized
 * uncompressed and never cross either wire — that is why both numbers exist.)
 */
export function effectiveMaxBytes(ext, limits) {
  const typeCap = limits.fileSizeBytes?.[ext] ?? limits.defaultMaxBytes;
  // ⚖ T-5 — an ARCHIVE is no longer clamped by the WS frame when the HTTP
  // container path is advertised: its real ceiling is `maxContainerBytes`.
  // Keeping the WS number here would make the picker's per-type row say 18 MB
  // about a 50 MB zip the system accepts — understating in a way that stops a
  // lawyer trying something that works.
  //
  // ⚠ Presence-gated like every other use of this block. Absent ⇒ old runtime
  // ⇒ the WS clamp is the truth, exactly as before.
  const transportCap =
    limits.httpUpload && ARCHIVE_EXTS.has(ext) && limits.archive?.enabled !== false
      ? Math.max(limits.transportMaxBytes, limits.httpUpload.maxContainerBytes)
      : limits.transportMaxBytes;
  return Math.min(typeCap, transportCap);
}

/** Bytes → a pt-BR decimal string, e.g. 18.0 → "18,0". Formatting only — the
 *  caller owns the sentence this lands in. */
const MB = 1024 * 1024;
const mb = (bytes) => (bytes / MB).toFixed(1).replace(".", ",");

/**
 * Pre-validate one file. Returns `null` when the upload should proceed.
 *
 * Returns a refusal — never throws — so the caller decides how to surface it.
 * When `limits` is undefined (policy not fetched yet) this ALLOWS the upload:
 * the server still enforces, and blocking a lawyer's upload because a
 * read-only policy call has not landed would be a worse failure than a
 * round-trip rejection.
 */
export function preValidateUpload(file, limits) {
  const decision = selectUploadTransport(file, limits);
  return decision.transport === "refused" ? decision.refusal : null;
}

export function selectUploadTransport(file, limits) {
  const ext = extensionOf(file.name);
  const base = { name: file.name, ext, sizeMb: mb(file.size), limitMb: "" };
  const refuse = (code, limitMb) => ({
    transport: "refused",
    refusal: { code, context: { ...base, limitMb } },
  });

  // Empty files are rejected server-side too; catching it here avoids a
  // pointless round trip and a confusing "0 bytes written".
  if (file.size === 0) return refuse("emptyFile", "0");
  // Policy not fetched yet — allow, exactly as before: the server enforces, and
  // blocking a lawyer because a read-only policy call has not landed is worse.
  if (!limits) return { transport: "ws" };

  // 1 — denylist. Refuse regardless of size or transport.
  const denied = new Set((limits.deniedExtensions ?? []).map((e) => e.toLowerCase()));
  if (ext && denied.has(ext)) {
    return { transport: "refused", refusal: { code: "deniedExtension", context: base } };
  }

  // 2 — over the WS ceiling: the HTTP container path is the only way through.
  if (file.size > limits.transportMaxBytes) {
    const http = limits.httpUpload;
    const isArchive = ARCHIVE_EXTS.has(ext) && limits.archive?.enabled !== false;
    // ⚠ PRESENCE, not optimism — see the docblock. Absent ⇒ pre-T-5 behaviour.
    // `archiveOnly` is honoured as served: a large non-archive is still refused
    // at the WS ceiling, because this route will not take it either.
    if (!http || (http.archiveOnly && !isArchive)) {
      return refuse("transportExceeded", mb(limits.transportMaxBytes));
    }
    if (file.size > http.maxContainerBytes) {
      return refuse("containerExceeded", mb(http.maxContainerBytes));
    }
    return { transport: "http", path: http.path };
  }

  // 3 — per-type cap, quoted as the EFFECTIVE limit. Only reachable UNDER the
  // WS ceiling, so it keeps describing the WS path exactly as it always did.
  const effective = effectiveMaxBytes(ext, limits);
  if (file.size > effective) return refuse("typeCapExceeded", mb(effective));

  return { transport: "ws" };
}
